import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal, Spinner, Table } from "react-bootstrap";
import { FiCalendar, FiEdit2, FiMessageCircle, FiPlus, FiUsers } from "react-icons/fi";

import { getApiUrl } from "../../utils/apiUrl";
import { getCsrfToken } from "../../utils/getCsrfToken";
import PersonaIniciativa from "./PersonaIniciativa";

const participantKey = (item) => String(item.id);

const normalizeDate = (value) => {
  if (!value) return "";
  const parts = value.split("-");
  if (parts.length !== 3) return "";
  return parts[0].length === 4 ? value : parts.reverse().join("-");
};

const formatDate = (value) => {
  const normalized = normalizeDate(value);
  if (!normalized) return "Sin definir";
  const [year, month, day] = normalized.split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString("es-CL");
};

const responseError = (body, fallback) => {
  const error = body.fecha?.[0]
    || body.asistentes?.[0]
    || body.non_field_errors?.[0]
    || body.detail;
  return error || fallback;
};

const SeccionAsistencia = ({ iniciativa }) => {
  const participants = useMemo(
    () => (iniciativa.participaciones || [])
      .filter((item) => item.inscrita && item.persona)
      .map((item) => item.persona),
    [iniciativa.participaciones],
  );
  const minimumDate = normalizeDate(iniciativa.f_inicio);
  const maximumDate = normalizeDate(iniciativa.f_termino);
  const [records, setRecords] = useState([]);
  const [fecha, setFecha] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [editing, setEditing] = useState(null);
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [excuseRecord, setExcuseRecord] = useState(null);
  const [showExcuseModal, setShowExcuseModal] = useState(false);
  const [excuses, setExcuses] = useState([]);
  const [loadingExcuses, setLoadingExcuses] = useState(false);
  const [excuseParticipant, setExcuseParticipant] = useState("");
  const [excuseText, setExcuseText] = useState("");
  const [savingAttendance, setSavingAttendance] = useState(false);
  const [savingExcuse, setSavingExcuse] = useState(false);

  const attendanceDateWarning = useMemo(() => {
    if (!fecha) return "Selecciona una fecha válida antes de guardar la asistencia.";
    if (minimumDate && fecha < minimumDate) {
      return `La fecha debe ser igual o posterior al ${formatDate(minimumDate)}.`;
    }
    if (maximumDate && fecha > maximumDate) {
      return `La fecha debe ser igual o anterior al ${formatDate(maximumDate)}.`;
    }
    return "";
  }, [fecha, maximumDate, minimumDate]);

  const excusedParticipantIds = useMemo(
    () => new Set(excuses.map((item) => String(item.persona))),
    [excuses],
  );
  const availableAbsentParticipants = useMemo(
    () => (excuseRecord?.participantes_inasistentes || [])
      .filter((item) => !excusedParticipantIds.has(participantKey(item))),
    [excuseRecord, excusedParticipantIds],
  );

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(
        getApiUrl(`api/iniciativas-asistencias/?iniciativa=${iniciativa.id}`),
        { credentials: "include" },
      );
      if (!response.ok) throw new Error("No se pudieron cargar las asistencias.");
      setRecords(await response.json());
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, [iniciativa.id]);

  useEffect(() => { loadRecords(); }, [loadRecords]);

  const toggle = (participant) => {
    const key = participantKey(participant);
    setSelected((current) => {
      const next = new Set(current);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const closeAttendanceModal = () => {
    if (savingAttendance) return;
    setShowAttendanceModal(false);
    setFecha("");
    setSelected(new Set());
    setEditing(null);
    setError("");
  };

  const openNewAttendance = () => {
    setEditing(null);
    setFecha("");
    setSelected(new Set());
    setError("");
    setShowAttendanceModal(true);
  };

  const editRecord = (record) => {
    setEditing(record);
    setFecha(normalizeDate(record.fecha));
    setSelected(new Set(record.asistentes_detalle.map((item) => participantKey(item))));
    setError("");
    setShowAttendanceModal(true);
  };

  const save = async () => {
    if (attendanceDateWarning) {
      setError(attendanceDateWarning);
      return;
    }

    setError("");
    setSavingAttendance(true);
    const asistentes = participants.filter((item) => selected.has(participantKey(item)));
    try {
      const csrfToken = await getCsrfToken();
      const response = await fetch(
        getApiUrl(`api/iniciativas-asistencias/${editing ? `${editing.id}/` : ""}`),
        {
          method: editing ? "PATCH" : "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json", "X-CSRFToken": csrfToken },
          body: JSON.stringify({
            iniciativa: iniciativa.id,
            fecha,
            asistentes: asistentes.map((item) => item.id),
          }),
        },
      );
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(responseError(body, "No se pudo guardar la asistencia."));
      }
      setShowAttendanceModal(false);
      setFecha("");
      setSelected(new Set());
      setEditing(null);
      await loadRecords();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSavingAttendance(false);
    }
  };

  const openExcuses = async (record) => {
    setExcuseRecord(record);
    setExcuseParticipant("");
    setExcuseText("");
    setExcuses([]);
    setError("");
    setLoadingExcuses(true);
    setShowExcuseModal(true);
    try {
      const response = await fetch(
        getApiUrl(`api/iniciativas-excusas/?asistencia=${record.id}`),
        { credentials: "include" },
      );
      if (!response.ok) throw new Error("No se pudieron cargar las excusas.");
      setExcuses(await response.json());
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoadingExcuses(false);
    }
  };

  const closeExcuseModal = () => {
    if (savingExcuse) return;
    setShowExcuseModal(false);
    setExcuseRecord(null);
    setExcuses([]);
    setExcuseParticipant("");
    setExcuseText("");
    setError("");
  };

  const saveExcuse = async () => {
    const participant = availableAbsentParticipants.find(
      (item) => participantKey(item) === excuseParticipant,
    );
    if (!participant || !excuseText.trim()) return;
    setError("");
    setSavingExcuse(true);
    try {
      const csrfToken = await getCsrfToken();
      const response = await fetch(getApiUrl("api/iniciativas-excusas/"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-CSRFToken": csrfToken },
        body: JSON.stringify({
          asistencia: excuseRecord.id,
          persona: participant.id,
          excusa_texto: excuseText.trim(),
        }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(responseError(body, "No se pudo guardar la excusa."));
      }
      setExcuses((current) => [...current, body]);
      setExcuseParticipant("");
      setExcuseText("");
      await loadRecords();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSavingExcuse(false);
    }
  };

  return (
    <div className="actividad-info initiative-attendance-section">
      <div className="initiative-section-heading initiative-attendance-heading">
        <div>
          <span>SEGUIMIENTO</span>
          <h5>Asistencia</h5>
          <p>Registra la asistencia y las excusas de cada jornada.</p>
        </div>
        <Button
          type="button"
          className="initiative-attendance-primary"
          onClick={openNewAttendance}
          disabled={savingAttendance || savingExcuse}
        >
          <FiPlus /> Registrar asistencia
        </Button>
      </div>

      {error && !showAttendanceModal && !showExcuseModal && (
        <Alert variant="danger">{error}</Alert>
      )}

      {loading ? (
        <div className="initiative-attendance-loading">
          <Spinner animation="border" size="sm" /> Cargando jornadas…
        </div>
      ) : records.length === 0 ? (
        <div className="initiative-attendance-empty">
          <FiCalendar />
          <strong>Sin jornadas registradas</strong>
          <span>Usa “Registrar asistencia” para agregar la primera fecha.</span>
        </div>
      ) : (
        <div className="initiative-attendance-table-wrap">
          <Table responsive className="initiative-attendance-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Asistentes</th>
                <th>Excusas</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {records.map((record) => (
                <tr key={record.id}>
                  <td><strong>{record.fecha_formateada}</strong></td>
                  <td>
                    <span className="initiative-attendance-count count-present">
                      <FiUsers /> {record.asistentes_detalle.length}
                    </span>
                  </td>
                  <td>
                    <span className="initiative-attendance-count count-excused">
                      <FiMessageCircle /> {record.excusas_count || 0}
                    </span>
                  </td>
                  <td>
                    <div className="initiative-attendance-actions">
                      <Button
                        type="button"
                        className="attendance-action attendance-action-edit"
                        onClick={() => editRecord(record)}
                        disabled={savingAttendance || savingExcuse}
                      >
                        <FiEdit2 /> Editar
                      </Button>
                      <Button
                        type="button"
                        className="attendance-action attendance-action-excuse"
                        onClick={() => openExcuses(record)}
                        disabled={savingAttendance || savingExcuse}
                      >
                        <FiMessageCircle /> Excusas
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      <Modal
        show={showAttendanceModal}
        onHide={closeAttendanceModal}
        centered
        scrollable
        size="xl"
        className="initiative-attendance-modal"
      >
        <Modal.Header closeButton={!savingAttendance}>
          <div>
            <span>ASISTENCIA</span>
            <Modal.Title>{editing ? `Editar jornada del ${editing.fecha_formateada}` : "Registrar jornada"}</Modal.Title>
          </div>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <Form.Group className="initiative-attendance-date">
            <Form.Label>Fecha</Form.Label>
            <Form.Control
              type="date"
              value={fecha}
              min={minimumDate || undefined}
              max={maximumDate || undefined}
              disabled={Boolean(editing) || savingAttendance}
              onChange={(event) => {
                setFecha(event.target.value);
                setError("");
              }}
              isInvalid={Boolean(fecha && attendanceDateWarning)}
            />
            <Form.Text>
              Rango de la iniciativa: {formatDate(minimumDate)}–{formatDate(maximumDate)}.
            </Form.Text>
          </Form.Group>

          {attendanceDateWarning && (
            <Alert variant="warning" className="initiative-attendance-warning">
              {attendanceDateWarning} La asistencia todavía no se puede guardar.
            </Alert>
          )}

          <div className="initiative-attendance-selection-heading">
            <div>
              <strong>Participantes presentes</strong>
              <span>Selecciona a quienes asistieron a esta jornada.</span>
            </div>
            <b>{selected.size} seleccionadas</b>
          </div>
          {participants.length === 0 ? (
            <p className="initiative-empty-copy mb-0">
              Esta iniciativa todavía no tiene participantes inscritas.
            </p>
          ) : (
            <div className="initiative-attendance-people">
              {participants.map((participant) => (
                <div className="initiative-attendance-person" key={participantKey(participant)}>
                  <Form.Check
                    type="checkbox"
                    checked={selected.has(participantKey(participant))}
                    onChange={() => toggle(participant)}
                    disabled={savingAttendance}
                    aria-label={`Asistencia de ${participant.nombre}`}
                  />
                  <PersonaIniciativa
                    persona={participant}
                    detalle={(participant.roles || ["Persona"]).join(", ")}
                    compact
                  />
                </div>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            type="button"
            className="initiative-modal-cancel"
            onClick={closeAttendanceModal}
            disabled={savingAttendance}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            className="initiative-modal-save"
            onClick={save}
            disabled={Boolean(attendanceDateWarning) || savingAttendance}
          >
            {savingAttendance && <Spinner animation="border" size="sm" />}
            {editing ? "Guardar cambios" : "Registrar asistencia"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showExcuseModal}
        onHide={closeExcuseModal}
        centered
        scrollable
        size="lg"
        className="initiative-attendance-modal initiative-excuse-modal"
      >
        <Modal.Header closeButton={!savingExcuse}>
          <div>
            <span>JUSTIFICACIONES</span>
            <Modal.Title>Excusas del {excuseRecord?.fecha_formateada}</Modal.Title>
          </div>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {loadingExcuses ? (
            <div className="initiative-attendance-loading">
              <Spinner animation="border" size="sm" /> Cargando excusas…
            </div>
          ) : (
            <>
              <section className="initiative-excuse-form">
                <h6>Agregar excusa</h6>
                {availableAbsentParticipants.length > 0 ? (
                  <>
                    <Form.Group>
                      <Form.Label>Participante ausente</Form.Label>
                      <Form.Select
                        value={excuseParticipant}
                        onChange={(event) => setExcuseParticipant(event.target.value)}
                        disabled={savingExcuse}
                      >
                        <option value="">Selecciona una participante</option>
                        {availableAbsentParticipants.map((participant) => (
                          <option key={participantKey(participant)} value={participantKey(participant)}>
                            {participant.nombre} ({(participant.roles || ["Persona"]).join(", ")})
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Motivo</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={excuseText}
                        placeholder="Escribe la excusa informada…"
                        onChange={(event) => setExcuseText(event.target.value)}
                        disabled={savingExcuse}
                      />
                    </Form.Group>
                    <Button
                      type="button"
                      className="initiative-modal-save"
                      onClick={saveExcuse}
                      disabled={!excuseParticipant || !excuseText.trim() || savingExcuse}
                    >
                      {savingExcuse && <Spinner animation="border" size="sm" />}
                      Guardar excusa
                    </Button>
                  </>
                ) : (
                  <p className="initiative-empty-copy mb-0">
                    No quedan participantes ausentes sin una excusa registrada.
                  </p>
                )}
              </section>

              <section className="initiative-saved-excuses">
                <div>
                  <h6>Excusas guardadas</h6>
                  <span>{excuses.length}</span>
                </div>
                {excuses.length > 0 ? (
                  <ul className="initiative-excuse-list">
                    {excuses.map((excuse) => (
                      <li key={excuse.id}>
                        <PersonaIniciativa persona={excuse.participante} compact />
                        <p>{excuse.excusa_texto}</p>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="initiative-empty-copy mb-0">No hay excusas guardadas para esta fecha.</p>
                )}
              </section>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            type="button"
            className="initiative-modal-cancel"
            onClick={closeExcuseModal}
            disabled={savingExcuse}
          >
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SeccionAsistencia;
